import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import WalletPageShell from "../../Components/Wallet/WalletPageShell";
import {
  addClientAllowedIp,
  deleteClientAllowedIp,
  fetchClient,
  setClientApiSecret,
  updateClient,
} from "../../Apis/Wallet-api";
import { formatLabel, formatMoney } from "../../Utils/walletFormat";

export default function WalletClientDetailPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const id = Number(clientId);

  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState(null);
  const [name, setName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ipDialog, setIpDialog] = useState({ open: false, ip: "", label: "" });
  const [secretDialog, setSecretDialog] = useState({
    open: false,
    mode: "generate",
    secret: "",
    confirm: "",
  });
  const [secretResult, setSecretResult] = useState(null);
  const [secretSaving, setSecretSaving] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await fetchClient(id);
      setClient(data);
      setName(data?.client_name || "");
      setIsActive(data?.is_active !== false);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load client.");
      setClient(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!name.trim()) {
      toast.error("Client name is required.");
      return;
    }
    setSaving(true);
    try {
      const data = await updateClient(id, {
        client_name: name.trim(),
        is_active: isActive,
      });
      setClient(data);
      setName(data?.client_name || "");
      setIsActive(data?.is_active !== false);
      toast.success("Client updated.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Update failed.");
    } finally {
      setSaving(false);
    }
  };

  const copyText = async (label, value) => {
    try {
      await navigator.clipboard.writeText(String(value));
      toast.success(`${label} copied.`);
    } catch {
      toast.error("Could not copy to clipboard.");
    }
  };

  const submitSecret = async () => {
    if (secretDialog.mode === "custom") {
      if (!secretDialog.secret || secretDialog.secret.length < 16) {
        toast.error("Secret must be at least 16 characters.");
        return;
      }
      if (secretDialog.secret !== secretDialog.confirm) {
        toast.error("Secret confirmation does not match.");
        return;
      }
    }
    setSecretSaving(true);
    try {
      const result = await setClientApiSecret(
        id,
        secretDialog.mode === "custom" ? secretDialog.secret : undefined
      );
      setSecretResult(result);
      toast.success(
        result.rotated ? "API secret rotated." : "API secret set."
      );
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to set secret.");
    } finally {
      setSecretSaving(false);
    }
  };

  if (loading) {
    return (
      <WalletPageShell heading="Client" subtitle="Loading…">
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      </WalletPageShell>
    );
  }

  if (!client) {
    return (
      <WalletPageShell heading="Client" subtitle="Not found">
        <Button onClick={() => navigate("/wallet/clients")}>Back to clients</Button>
      </WalletPageShell>
    );
  }

  const apps = client.external_applications || [];
  const ips = client.allowed_ips || [];

  return (
    <WalletPageShell
      heading={client.client_name || `Client #${id}`}
      subtitle={`Client #${id} — API credentials, IP allowlist, and linked wallet agents`}
    >
      <Stack spacing={3}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Details
          </Typography>
          <Stack spacing={2} maxWidth={480}>
            <TextField
              label="Client name"
              size="small"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              }
              label="Active"
            />
            <Stack direction="row" spacing={1}>
              <Button variant="contained" disabled={saving} onClick={save}>
                {saving ? "Saving…" : "Save"}
              </Button>
              <Button onClick={() => navigate("/wallet/clients")}>Back</Button>
            </Stack>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Client API credentials
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            For the client&apos;s own system integration. Headers:{" "}
            <code>X-Api-Key</code> / <code>X-Api-Secret</code> on{" "}
            <code>/api/client/*</code>. Secret is never shown here after
            create/rotate. Agent wallet APIs still use each application&apos;s
            own credentials.
          </Typography>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                API key (<code>X-Api-Key</code>)
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  variant="body2"
                  fontFamily="monospace"
                  sx={{ wordBreak: "break-all", flex: 1 }}
                >
                  {client.api_key || "—"}
                </Typography>
                {client.api_key && (
                  <Button
                    size="small"
                    onClick={() => copyText("API key", client.api_key)}
                  >
                    Copy
                  </Button>
                )}
              </Stack>
            </Box>
            <Typography variant="caption" color="text.secondary">
              API secret (<code>X-Api-Secret</code>) —{" "}
              {client.has_api_secret ? "set" : "not set"} · rotate below
            </Typography>
            <Button
              size="small"
              variant="outlined"
              sx={{ alignSelf: "flex-start" }}
              onClick={() => {
                setSecretResult(null);
                setSecretDialog({
                  open: true,
                  mode: "generate",
                  secret: "",
                  confirm: "",
                });
              }}
            >
              {client.has_api_secret || client.api_key
                ? "Set / rotate secret"
                : "Issue credentials"}
            </Button>
          </Stack>
        </Paper>

        <Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle2">External applications</Typography>
            <Chip size="small" label={`${apps.length} agent(s)`} />
          </Stack>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Application user ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Balance</TableCell>
                  <TableCell align="right">Credit used</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apps.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        No agents linked to this client.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  apps.map((app) => (
                    <TableRow
                      key={app.id}
                      hover
                      sx={{ cursor: app.wallet_id ? "pointer" : "default" }}
                      onClick={() => {
                        if (app.wallet_id) {
                          navigate(`/wallet/applications/${app.wallet_id}`, {
                            state: {
                              appName: app.name,
                              externalAppId: app.id,
                            },
                          });
                        }
                      }}
                    >
                      <TableCell>{app.id}</TableCell>
                      <TableCell>{app.name}</TableCell>
                      <TableCell>
                        {app.application_user_id != null
                          ? app.application_user_id
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={formatLabel(app.status || "unknown")}
                          color={app.status === "active" ? "success" : "default"}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {formatMoney(app.balance)}
                      </TableCell>
                      <TableCell align="right">
                        {formatMoney(app.credit_used)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box>
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Allowed IPs are shared by all agents under this client (and client
            API calls). Empty list = open access when{" "}
            <code>WALLET_IP_ALLOWLIST_OPEN_WHEN_EMPTY=true</code>.
          </Alert>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle2">Allowed IPs</Typography>
            <Button
              size="small"
              variant="contained"
              onClick={() => setIpDialog({ open: true, ip: "", label: "" })}
            >
              Add IP
            </Button>
          </Stack>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>IP / CIDR</TableCell>
                  <TableCell>Label</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {ips.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">
                        No IPs configured — client/agent API is open for this
                        tenant.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  ips.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        <Typography fontFamily="monospace" variant="body2">
                          {row.ip_address}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.label || "—"}</TableCell>
                      <TableCell>
                        {row.created_at
                          ? new Date(row.created_at).toLocaleString()
                          : "—"}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          color="error"
                          onClick={async () => {
                            try {
                              await deleteClientAllowedIp(id, row.id);
                              await load();
                              toast.success("Allowed IP deleted.");
                            } catch (err) {
                              toast.error(
                                err.response?.data?.message || "Delete failed."
                              );
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Stack>

      <Dialog
        open={ipDialog.open}
        onClose={() => setIpDialog({ open: false, ip: "", label: "" })}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Add allowed IP</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="IP or CIDR"
              placeholder="127.0.0.1 or 10.0.0.0/24"
              value={ipDialog.ip}
              onChange={(e) => setIpDialog({ ...ipDialog, ip: e.target.value })}
            />
            <TextField
              fullWidth
              size="small"
              label="Label (optional)"
              value={ipDialog.label}
              onChange={(e) =>
                setIpDialog({ ...ipDialog, label: e.target.value })
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIpDialog({ open: false, ip: "", label: "" })}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!ipDialog.ip.trim()) {
                toast.error("IP address is required.");
                return;
              }
              try {
                await addClientAllowedIp(
                  id,
                  ipDialog.ip.trim(),
                  ipDialog.label.trim() || undefined
                );
                setIpDialog({ open: false, ip: "", label: "" });
                await load();
                toast.success("Allowed IP saved.");
              } catch (err) {
                toast.error(err.response?.data?.message || "Add failed.");
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={secretDialog.open}
        onClose={() => {
          if (!secretResult) {
            setSecretDialog({
              open: false,
              mode: "generate",
              secret: "",
              confirm: "",
            });
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {secretResult ? "API secret ready" : "Set / rotate client API secret"}
        </DialogTitle>
        <DialogContent>
          {secretResult ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="warning">
                Copy the API secret now — it is shown only once.
              </Alert>
              {secretResult.api_key && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    API key
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      sx={{ wordBreak: "break-all", flex: 1 }}
                    >
                      {secretResult.api_key}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() =>
                        copyText("API key", secretResult.api_key)
                      }
                    >
                      Copy
                    </Button>
                  </Stack>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">
                  API secret
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    variant="body2"
                    fontFamily="monospace"
                    sx={{ wordBreak: "break-all", flex: 1 }}
                  >
                    {secretResult.api_secret}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() =>
                      copyText("API secret", secretResult.api_secret)
                    }
                  >
                    Copy
                  </Button>
                </Stack>
              </Box>
            </Stack>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <RadioGroup
                value={secretDialog.mode}
                onChange={(e) =>
                  setSecretDialog({
                    ...secretDialog,
                    mode: e.target.value,
                    secret: "",
                    confirm: "",
                  })
                }
              >
                <FormControlLabel
                  value="generate"
                  control={<Radio size="small" />}
                  label="Generate a new secret"
                />
                <FormControlLabel
                  value="custom"
                  control={<Radio size="small" />}
                  label="Set a custom secret"
                />
              </RadioGroup>
              {secretDialog.mode === "custom" && (
                <>
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    label="Secret (min 16 chars)"
                    value={secretDialog.secret}
                    onChange={(e) =>
                      setSecretDialog({
                        ...secretDialog,
                        secret: e.target.value,
                      })
                    }
                  />
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    label="Confirm secret"
                    value={secretDialog.confirm}
                    onChange={(e) =>
                      setSecretDialog({
                        ...secretDialog,
                        confirm: e.target.value,
                      })
                    }
                  />
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {secretResult ? (
            <Button
              onClick={() => {
                setSecretResult(null);
                setSecretDialog({
                  open: false,
                  mode: "generate",
                  secret: "",
                  confirm: "",
                });
              }}
            >
              Done
            </Button>
          ) : (
            <>
              <Button
                onClick={() =>
                  setSecretDialog({
                    open: false,
                    mode: "generate",
                    secret: "",
                    confirm: "",
                  })
                }
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={secretSaving}
                onClick={submitSecret}
              >
                {secretSaving ? "Saving…" : "Confirm"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </WalletPageShell>
  );
}
